import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useAppSelector } from 'shared/store';
import { selectIncomingDisplayStream, RealtimeState } from 'shared/store/realtime-slice';
import { Trigger } from 'types';
import type { AppState } from 'shared/store/reducers';
import { createKit, getIntendedAssocVisitor } from './kit';
import { PeerStreamChange } from '.';
import { emitCustomRrwebStateEvent } from './rrweb-event-handler';

type PA<T> = PayloadAction<T>;

/**
 * Displayshare flow rough outline
 * 1) Reducer sets optimisticIsDisplayshare state
 * 2) requestDisplayshareEffect sends interaction update with is_displayshare = true
 * 3) Backend delivers sbid data update to widget with is_displayshare = true which opens request modal
 * 4) Widget then either:
 *   a) Accepts and sends a getDisplayMedia stream over webrtc
 *   b) Rejects and sends a trigger to frontend that sets optimisticIsDisplayshare / is_displayshare to false
 */

/****************************************************
 *
 *                 Thunks
 *
 ****************************************************/
export const requestDisplayshare = createAsyncThunk<void, void, { state: AppState }>(
  'realtime/requestDisplayshare',
  async (_, { getState }) => {
    if (getState().realtime.incomingDisplayStreamId) {
      throw new Error('Already screensharing');
    }
  },
);

export const endDisplayshare = createAsyncThunk<void, void, { state: AppState }>(
  'realtime/endDisplayshare',
  () => {
    // no-op, needs to be a thunk action due to triggerThunkHelper
  },
);

/****************************************************
 *
 *                 Reducers
 *
 ****************************************************/
const reducers = {
  incomingDisplayStreamChanged: (state: RealtimeState, action: PA<PeerStreamChange>) => {
    const { streamId, isReceivingVideo } = action.payload;
    state.incomingDisplayStreamId = isReceivingVideo ? streamId : null;
  },
};
// Access actions before createSlice so we can reference them locally
// const actions = createRealtimeReducerActions({ reducers });

export const displayshareKit = createKit({
  reducers,
  extraReducers: builder => {
    builder.addCase(requestDisplayshare.fulfilled, state => {
      state.optimisticInteractions = {
        ...state.optimisticInteractions,
        isDisplayshare: true,
      };
    });

    builder.addCase(endDisplayshare.fulfilled, state => {
      state.incomingDisplayStreamId = null;
      state.optimisticInteractions = {
        ...state.optimisticInteractions,
        isDisplayshare: false,
      };
    });
  },

  /****************************************************
   *
   *               Handle triggers
   *
   ****************************************************/
  triggerThunkHelper: (trigger, _, dispatch) => {
    if (
      trigger === Trigger.REJECT_CALL ||
      trigger === Trigger.REJECT_DISPLAYSHARE ||
      trigger === Trigger.STOP_DISPLAYSHARE
    ) {
      dispatch(endDisplayshare());
    }
  },

  /****************************************************
   *
   *         Visitor sync/update reducer helpers
   *
   ****************************************************/
  visitorUpdateReducer: (state: RealtimeState) => {
    const assocVis = getIntendedAssocVisitor(state);
    if (assocVis) {
      if (!assocVis.isDisplayshare) {
        state.incomingDisplayStreamId = null;
      }
    } else {
      state.incomingDisplayStreamId = null;
    }
  },

  /****************************************************
   *
   *               Kit hook
   *
   ****************************************************/
  useKitHook: () => {
    const { isAwaitingReplayerFirstRender } = useAppSelector(s => s.dashboard);
    const isDisplayshareStream = !!useAppSelector(selectIncomingDisplayStream);

    // Emit rrweb state to track if we're displaysharing or not.
    useEffect(
      function customRrwebDisplayshareEventsEffect() {
        if (!isAwaitingReplayerFirstRender) {
          emitCustomRrwebStateEvent({
            isDisplayshare: isDisplayshareStream,
          });
        }
      },
      [isDisplayshareStream, isAwaitingReplayerFirstRender],
    );
  },
});
